import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    COLLABORATION_METHOD_ASYNC,
    COLLABORATION_METHOD_OPTION_KEY,
    COLLABORATION_METHOD_SYNC,
    DESIGN_LIVE_REVIEW_METHOD,
    bugtracker,
} from '@99designs/design-services-common';
import { useBriefContext } from '../../../BriefContext';
import { DesignServiceRequestType } from '../../../Submit/consts';
import { useUpdateField } from '../useUpdateField';
import { useBriefSetGridChoiceMutation } from './briefSetGridChoice.generated';
import {
    ServiceOptionsUpdatedDocument,
    ServiceOptionsUpdatedMutation,
    ServiceOptionsUpdatedMutationVariables,
} from './serviceOptionsUpdated.generated';

export function useUpdateGridChoiceField(
    key: string,
    briefId?: string,
    initialValue?: string | null
) {
    const client = useApolloClient();
    const { serviceProductOptions } = useBriefContext();

    const [setGridChoice] = useBriefSetGridChoiceMutation();

    const updateField = useUpdateField<string>({
        key,
        setField: setGridChoice,
        briefId,
        initialValue,
    });

    return useCallback(
        async (value: string) => {
            const result = await updateField(value);
            const briefData = result?.data;
            if (!briefData?.setField) {
                return;
            }

            if (key !== 'requestType') {
                return;
            }

            const field = briefData.setField.dynamicFields.find((f) => f.id === key);
            if (!field) {
                return;
            }
            if (field.__typename !== 'GridChoiceField') {
                throw new Error(
                    "Field is not a GridChoiceField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                );
            }
            const choiceValue = field.choiceValue as DesignServiceRequestType | null;
            if (!choiceValue) {
                return;
            }

            const correlationId = briefData.setField.correlationId;
            if (!correlationId) {
                return;
            }

            const isDesignLive = choiceValue === DESIGN_LIVE_REVIEW_METHOD;
            const collaborationMethodValue = isDesignLive
                ? COLLABORATION_METHOD_SYNC
                : COLLABORATION_METHOD_ASYNC;

            const updatedOptions = (serviceProductOptions ?? []).map((option) =>
                option.key === COLLABORATION_METHOD_OPTION_KEY
                    ? { key: option.key, value: collaborationMethodValue }
                    : option
            );

            if (updatedOptions.length > 0) {
                try {
                    await client.mutate<
                        ServiceOptionsUpdatedMutation,
                        ServiceOptionsUpdatedMutationVariables
                    >({
                        mutation: ServiceOptionsUpdatedDocument,
                        variables: {
                            requestId: correlationId,
                            input: { options: updatedOptions },
                        },
                    });
                } catch (e) {
                    const error = e instanceof Error ? e : new Error(String(e));
                    bugtracker.notify(error);
                }
            }
        },
        [updateField, key, serviceProductOptions, client]
    );
}
